import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CreditCardIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Tenant } from './TenantTable';
import { fetchWithCsrf } from '../../utils/csrf';

interface KartraSubscription {
  id: number;
  kartra_subscription_id: string;
  internal_subscription_id: string | null;
  status: string;
  product_id: string;
  product_name: string;
  product_price_point: string | null;
  amount: number;
  billing_cycle: string;
  started_at: string | null;
  next_billing_date: string | null;
  cancelled_at: string | null;
  expires_at: string | null;
  payment_method: string | null;
  last_4_card_digits: string | null;
  total_payments: number;
  successful_payments: number;
  failed_payments: number;
  lead_id: string | null;
  lead_email: string | null;
}

interface ProductFeature {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  is_highlighted: boolean;
  sort_order: number;
}

interface KartraProduct {
  id: number;
  kartra_product_id: string;
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  is_popular: boolean;
  is_featured: boolean;
  monthly_price: number | null;
  annual_price: number | null;
  features: ProductFeature[];
  limits: Record<string, string>;
}

interface RecentTransaction {
  id: number;
  type: string;
  amount: number;
  product_name: string;
  status: string;
  date: string;
}

interface LimitOverrides {
  websites: number | null;
  analyses_per_month: number | null;
  team_members: number | null;
  storage_gb: number | null;
}

interface CurrentUsage {
  websites: number;
  analyses: number;
  team_members: number;
  storage_gb: number;
}

interface KartraSubscriptionData {
  has_subscription: boolean;
  user: {
    id: number;
    email: string;
    name: string;
  };
  tenant_id?: number;
  limit_overrides?: LimitOverrides;
  current_usage?: CurrentUsage;
  subscription?: KartraSubscription;
  product?: {
    id: number;
    slug: string;
    name: string;
    features: ProductFeature[];
    limits: Record<string, string>;
  };
  available_products: KartraProduct[];
  recent_transactions: RecentTransaction[];
  last_transaction?: RecentTransaction;
  message?: string;
}

interface SubscriptionManagementProps {
  isOpen: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onUpdateSubscription: (tenantId: string, planId: string, billingCycle: string) => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({
  isOpen,
  tenant,
  onClose,
  onUpdateSubscription
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<KartraSubscriptionData | null>(null);

  // Limit override form state
  const [limitOverrides, setLimitOverrides] = useState<LimitOverrides>({
    websites: null,
    analyses_per_month: null,
    team_members: null,
    storage_gb: null,
  });
  const [isSavingLimits, setIsSavingLimits] = useState(false);
  const [limitsModified, setLimitsModified] = useState(false);

  useEffect(() => {
    if (tenant && isOpen) {
      fetchSubscriptionData();
    }
  }, [tenant, isOpen]);

  // Update limit override form when data loads
  useEffect(() => {
    if (subscriptionData?.limit_overrides) {
      setLimitOverrides(subscriptionData.limit_overrides);
      setLimitsModified(false);
    }
  }, [subscriptionData]);

  const fetchSubscriptionData = async () => {
    if (!tenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithCsrf(`/api/admin/users/${tenant.id}/kartra-subscription`);

      if (!response.ok) {
        throw new Error('Failed to fetch subscription data');
      }

      const data = await response.json();
      setSubscriptionData(data);
    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
      setError('Failed to load subscription data from Kartra');
      toast.error('Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLimitChange = (field: keyof LimitOverrides, value: string) => {
    const numValue = value === '' ? null : parseInt(value, 10);
    setLimitOverrides(prev => ({
      ...prev,
      [field]: isNaN(numValue as number) ? null : numValue,
    }));
    setLimitsModified(true);
  };

  const saveLimitOverrides = async () => {
    if (!tenant) return;

    setIsSavingLimits(true);
    try {
      const response = await fetchWithCsrf(`/api/admin/users/${tenant.id}/limit-overrides`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(limitOverrides),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save limit overrides failed:', response.status, errorData);
        throw new Error(errorData.message || 'Failed to save limit overrides');
      }

      const data = await response.json();
      toast.success('Limit overrides saved successfully');
      setLimitsModified(false);

      // Update subscription data with new overrides
      if (subscriptionData) {
        setSubscriptionData({
          ...subscriptionData,
          limit_overrides: data.limit_overrides,
        });
      }
    } catch (err) {
      console.error('Failed to save limit overrides:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save limit overrides');
    } finally {
      setIsSavingLimits(false);
    }
  };

  const resetLimitOverrides = () => {
    if (subscriptionData?.limit_overrides) {
      setLimitOverrides(subscriptionData.limit_overrides);
      setLimitsModified(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      active: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: <CheckCircleIcon className="h-4 w-4 text-green-600" />
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: <XCircleIcon className="h-4 w-4 text-red-600" />
      },
      expired: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        icon: <ClockIcon className="h-4 w-4 text-gray-600" />
      },
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: <ClockIcon className="h-4 w-4 text-yellow-600" />
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTransactionTypeBadge = (type: string) => {
    const typeConfig: Record<string, { bg: string; text: string }> = {
      sale: { bg: 'bg-green-100', text: 'text-green-800' },
      rebill: { bg: 'bg-blue-100', text: 'text-blue-800' },
      refund: { bg: 'bg-red-100', text: 'text-red-800' },
      partial_refund: { bg: 'bg-orange-100', text: 'text-orange-800' },
      chargeback: { bg: 'bg-red-100', text: 'text-red-800' },
      failed: { bg: 'bg-gray-100', text: 'text-gray-800' },
    };

    const config = typeConfig[type] || { bg: 'bg-gray-100', text: 'text-gray-800' };

    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {type.replace('_', ' ')}
      </span>
    );
  };

  if (!isOpen || !tenant) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CreditCardIcon className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Subscription Management</h3>
                  <p className="text-sm text-gray-500">
                    {tenant.first_name} {tenant.last_name} - {tenant.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchSubscriptionData}
                  disabled={isLoading}
                  className="p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-100"
                  title="Refresh data"
                >
                  <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-500">Loading Kartra subscription data...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mb-4" />
                <p className="text-gray-700 font-medium mb-2">Failed to Load Data</p>
                <p className="text-gray-500 text-sm mb-4">{error}</p>
                <button
                  onClick={fetchSubscriptionData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Try Again
                </button>
              </div>
            ) : subscriptionData ? (
              <div className="space-y-6">
                {/* Current Subscription Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                    Kartra Subscription
                  </h4>

                  {subscriptionData.has_subscription && subscriptionData.subscription ? (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h5 className="text-lg font-semibold text-gray-900">
                            {subscriptionData.subscription.product_name}
                          </h5>
                          <p className="text-sm text-gray-600">
                            {subscriptionData.subscription.product_price_point || 'Standard'}
                          </p>
                        </div>
                        {getStatusBadge(subscriptionData.subscription.status)}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Amount</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {formatCurrency(subscriptionData.subscription.amount)}
                            <span className="text-sm font-normal text-gray-500">
                              /{subscriptionData.subscription.billing_cycle || 'month'}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Started</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(subscriptionData.subscription.started_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Next Billing</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(subscriptionData.subscription.next_billing_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Payments</p>
                          <p className="text-sm font-medium text-gray-900">
                            {subscriptionData.subscription.successful_payments} successful
                            {subscriptionData.subscription.failed_payments > 0 && (
                              <span className="text-red-600"> / {subscriptionData.subscription.failed_payments} failed</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Payment Method */}
                      {subscriptionData.subscription.last_4_card_digits && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 bg-white/50 rounded px-3 py-2">
                          <CreditCardIcon className="h-4 w-4" />
                          <span>
                            {subscriptionData.subscription.payment_method || 'Card'} ending in{' '}
                            <span className="font-mono font-medium">
                              {subscriptionData.subscription.last_4_card_digits}
                            </span>
                          </span>
                        </div>
                      )}

                      {/* Kartra IDs */}
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs text-gray-500 mb-2">Kartra Reference IDs</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="bg-white/70 px-2 py-1 rounded font-mono">
                            Sub: {subscriptionData.subscription.kartra_subscription_id || 'N/A'}
                          </span>
                          {subscriptionData.subscription.lead_id && (
                            <span className="bg-white/70 px-2 py-1 rounded font-mono">
                              Lead: {subscriptionData.subscription.lead_id}
                            </span>
                          )}
                          {subscriptionData.subscription.internal_subscription_id && (
                            <span className="bg-white/70 px-2 py-1 rounded font-mono">
                              Internal: {subscriptionData.subscription.internal_subscription_id}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Cancelled warning */}
                      {subscriptionData.subscription.cancelled_at && (
                        <div className="mt-4 flex items-start gap-2 bg-red-50 text-red-700 px-4 py-3 rounded-lg">
                          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">Subscription Cancelled</p>
                            <p className="text-sm">
                              Cancelled on {formatDate(subscriptionData.subscription.cancelled_at)}
                              {subscriptionData.subscription.expires_at && (
                                <> - Access until {formatDate(subscriptionData.subscription.expires_at)}</>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">No Active Kartra Subscription</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {subscriptionData.message || 'This user does not have a subscription in Kartra.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Product Features Section */}
                {subscriptionData.product && subscriptionData.product.features.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                      Current Plan Features
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-4">{subscriptionData.product.name}</h5>

                      {/* Highlighted Features */}
                      {subscriptionData.product.features.some(f => f.is_highlighted) && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 uppercase mb-2">Highlighted Features</p>
                          <div className="grid md:grid-cols-2 gap-3">
                            {subscriptionData.product.features
                              .filter(f => f.is_highlighted)
                              .map((feature) => (
                                <div
                                  key={feature.id}
                                  className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg"
                                >
                                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    {feature.icon ? (
                                      <span className="text-blue-600 text-sm">{feature.icon}</span>
                                    ) : (
                                      <CheckIcon className="h-4 w-4 text-blue-600" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900 text-sm">{feature.name}</p>
                                    {feature.description && (
                                      <p className="text-xs text-gray-600 mt-0.5">{feature.description}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-2">All Features</p>
                          <ul className="space-y-2">
                            {subscriptionData.product.features.map((feature) => (
                              <li key={feature.id} className="flex items-start gap-2">
                                <div className="flex-shrink-0 mt-0.5">
                                  {feature.icon ? (
                                    <span className="text-green-600 text-sm">{feature.icon}</span>
                                  ) : (
                                    <CheckIcon className="h-4 w-4 text-green-500" />
                                  )}
                                </div>
                                <div>
                                  <span className={`text-sm ${feature.is_highlighted ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                    {feature.name}
                                  </span>
                                  {feature.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase mb-2">Limits</p>
                          <ul className="space-y-2">
                            {Object.entries(subscriptionData.product.limits).map(([key, value]) => (
                              <li key={key} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                                <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="font-medium text-gray-900">{value}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Transactions */}
                {subscriptionData.recent_transactions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                      Recent Transactions
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {subscriptionData.recent_transactions.map((tx) => (
                            <tr key={tx.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatDate(tx.date)}
                              </td>
                              <td className="px-4 py-3">
                                {getTransactionTypeBadge(tx.type)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {tx.product_name}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                                {formatCurrency(tx.amount)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  tx.status === 'processed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {tx.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Available Products Section */}
                {subscriptionData.available_products.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                      Available Products (from Kartra)
                    </h4>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {subscriptionData.available_products.map((product) => {
                        const isCurrentProduct = subscriptionData.subscription?.product_id === product.kartra_product_id;

                        return (
                          <div
                            key={product.id}
                            className={`relative border rounded-lg p-4 ${
                              isCurrentProduct
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white'
                            } ${product.is_popular ? 'ring-2 ring-blue-500' : ''}`}
                          >
                            {product.is_popular && (
                              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                                <span className="bg-blue-500 text-white px-2 py-0.5 text-xs font-medium rounded-full">
                                  Popular
                                </span>
                              </div>
                            )}
                            {isCurrentProduct && (
                              <div className="absolute -top-2 right-2">
                                <span className="bg-green-500 text-white px-2 py-0.5 text-xs font-medium rounded-full">
                                  Current
                                </span>
                              </div>
                            )}

                            <h5 className="font-semibold text-gray-900 mt-1">{product.name}</h5>
                            {product.tagline && (
                              <p className="text-xs text-gray-500 mt-1">{product.tagline}</p>
                            )}

                            <div className="mt-3">
                              {product.monthly_price !== null && (
                                <p className="text-lg font-bold text-gray-900">
                                  {formatCurrency(product.monthly_price)}
                                  <span className="text-sm font-normal text-gray-500">/mo</span>
                                </p>
                              )}
                              {product.annual_price !== null && (
                                <p className="text-xs text-gray-500">
                                  or {formatCurrency(product.annual_price)}/year
                                </p>
                              )}
                            </div>

                            {product.features.length > 0 && (
                              <ul className="mt-3 space-y-1.5">
                                {product.features
                                  .filter(f => f.is_highlighted)
                                  .slice(0, 4)
                                  .map((feature) => (
                                    <li key={feature.id} className="flex items-start gap-1.5 text-xs">
                                      {feature.icon ? (
                                        <span className="text-green-600 flex-shrink-0">{feature.icon}</span>
                                      ) : (
                                        <CheckIcon className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                      )}
                                      <span className="text-gray-700 font-medium">{feature.name}</span>
                                    </li>
                                  ))}
                                {product.features.filter(f => !f.is_highlighted).slice(0, Math.max(0, 4 - product.features.filter(f => f.is_highlighted).length)).map((feature) => (
                                  <li key={feature.id} className="flex items-start gap-1.5 text-xs">
                                    {feature.icon ? (
                                      <span className="text-green-600 flex-shrink-0">{feature.icon}</span>
                                    ) : (
                                      <CheckIcon className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                    )}
                                    <span className="text-gray-600">{feature.name}</span>
                                  </li>
                                ))}
                                {product.features.length > 4 && (
                                  <li className="text-xs text-gray-400 pl-4">
                                    +{product.features.length - 4} more features
                                  </li>
                                )}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Limit Overrides Section */}
                {subscriptionData.tenant_id && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                      Account Limit Overrides
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-4">
                        Override the default product limits for this account. Empty = unlimited for team members. Storage defaults to 2GB.
                      </p>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        {/* Websites */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Websites
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              value={limitOverrides.websites ?? ''}
                              onChange={(e) => handleLimitChange('websites', e.target.value)}
                              placeholder="Default"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            {subscriptionData.current_usage && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                Using: {subscriptionData.current_usage.websites}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Analyses per Month */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Analyses per Month
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              value={limitOverrides.analyses_per_month ?? ''}
                              onChange={(e) => handleLimitChange('analyses_per_month', e.target.value)}
                              placeholder="Default"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            {subscriptionData.current_usage && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                Using: {subscriptionData.current_usage.analyses}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Team Members */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Team Members <span className="text-xs text-gray-400 font-normal">(empty = unlimited)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              value={limitOverrides.team_members ?? ''}
                              onChange={(e) => handleLimitChange('team_members', e.target.value)}
                              placeholder="Unlimited"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            {subscriptionData.current_usage && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                Using: {subscriptionData.current_usage.team_members}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Storage GB */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Storage (GB) <span className="text-xs text-gray-400 font-normal">(default: 2GB)</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              value={limitOverrides.storage_gb ?? ''}
                              onChange={(e) => handleLimitChange('storage_gb', e.target.value)}
                              placeholder="2"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            {subscriptionData.current_usage && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                Using: {subscriptionData.current_usage.storage_gb.toFixed(2)} GB
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          {limitsModified ? (
                            <span className="text-amber-600 font-medium">Unsaved changes</span>
                          ) : (
                            'Limits synced with database'
                          )}
                        </p>
                        <div className="flex gap-2">
                          {limitsModified && (
                            <button
                              onClick={resetLimitOverrides}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              Reset
                            </button>
                          )}
                          <button
                            onClick={saveLimitOverrides}
                            disabled={isSavingLimits || !limitsModified}
                            className={`px-4 py-1.5 text-sm font-medium text-white rounded-md ${
                              isSavingLimits || !limitsModified
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                          >
                            {isSavingLimits ? 'Saving...' : 'Save Limits'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info Note */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Subscription Management via Kartra</p>
                      <p>
                        Subscription changes must be made directly in Kartra. This view shows the current
                        subscription status synced from Kartra via IPN webhooks. Changes made in Kartra
                        will automatically sync to this system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
